import {createContext, useContext, useEffect, useState} from "react";
import {Product} from "../rest/jtmc/models/Product";
import Selective from "../rest/jtmc/models/Selective";
import Additive from "../rest/jtmc/models/Additive";
import AppConfigContext from "./AppConfigContext";
import OrderItem from "../rest/jtmc/models/OrderItem";
import Order from "../rest/jtmc/models/Order";
import {OrderContext} from "./OrderContext";
import {Variant} from "../rest/jtmc/models/Variant";

export type CartItem = {
    product: Product,
    variant: Variant | null,
    fullName: string,
    count: number,
    selective: Selective | null,
    additives: Additive[],
    singleItemSumPrice: (discount:boolean)=>number,
    totalItemSumPrice: (discount:boolean)=>number
};

type ContextValues = {
    cartItems: CartItem[],
    addToCart: (p: Product, v: Variant | null, s: Selective | null, a: Additive[]) => void,
    removeFromCart: (p: Product, v: Variant | null, s: Selective | null, a: Additive[] ) => void,
    getCount: (p: Product) => number,
    isFetching: boolean,
    allItemsCount: number,
    allItemsPrice: number,
    allItemsPriceNoDiscount: number,
    increase: (ci: CartItem) => void,
    decrease: (ci: CartItem) => void,
    delete: (ci: CartItem) => void,
    totalCartPriceHasDiscount: boolean
}

export const CartContext = createContext({} as ContextValues);

export function CartProvider({children}: any) {
    const [items, setItems] = useState<CartItem[]>([]);
    const [fetching, setFetching] = useState(false);
    const orderContext = useContext(OrderContext);

    useEffect(() => {
        if (orderContext.order != null)
            setItems(orderContext.order.items.map(item => orderItemToCartItem(item)));
        else setItems([]);
    }, [orderContext.order]);

    const itemPerformFetchMethod = useContext(AppConfigContext).fetch.Order.requests.performItems;
    const snack = useContext(AppConfigContext).snackBarData;

    const add = (p: Product, v: Variant| null, s: Selective | null, a: Additive[]) => {
        fetchItem(p, v, s, a, 1);
    }

    const remove = (p: Product,  v: Variant| null, s: Selective | null, a: Additive[]) => {
        fetchItem(p, v, s, a, -1);
    }

    const count = (p: Product) => {
        let c = 0;
        let matchId = p.isVariant ? p.parentProductId : p.id;
        items.map((cartItem, index) => {
            let idForMatch = cartItem.product.id;
            if (idForMatch == matchId)
                c += cartItem.count;
        });
        return c;
    }


    const hasProduct = (p: Product): boolean => {
        let has = false;
        items.map((item, index) => {
            if (item.product == p)
                has = true;
        });
        return has;
    }

    const allCount: () => number = () => {
        let c = 0;
        items.forEach((item) => {
            c += item.count;
        });
        return c;
    }

    const fetchItem = (p: Product, v: Variant | null, s: Selective | null, a: Additive[], count: number) => {
        setFetching(true);
        itemPerformFetchMethod({
            data: JSON.stringify({
                items: [
                    {
                        id: p.id,
                        variant_id: v ? v.id : null,
                        count: count,
                        selective: s?.id,
                        additives: a?.map(adtv => adtv?.id)
                    }
                ]
            })
        }).then((order: Order) => {
            orderContext.setOrder(order);
            setFetching(false);
        }).catch((err: string) => {
            setFetching(false);
        });
    }

    const allPriceWithDiscount = () => {
        let rv = 0;
        items.forEach((item, index) => {
            const productOrVariant = item.variant != null ? item.variant : item.product;
            const pPrice = productOrVariant.priceWithDiscount;
            const sPrice = item.selective?.price ?? 0;
            let aPrice = 0;
            item.additives?.map(additive => aPrice += additive.price);
            const packPrice = pPrice + sPrice + aPrice;
            rv += packPrice * item.count;
        });
        return rv;
    }

    const allPriceWithoutDiscount = () => {
        let rv = 0;
        items.forEach((item, index) => {
            const productOrVariant = item.variant != null ? item.variant : item.product;
            const pPrice = productOrVariant.price;
            const sPrice = item.selective?.price ?? 0;
            let aPrice = 0;
            item.additives?.map(additive => aPrice += additive.price);
            const packPrice = pPrice + sPrice + aPrice;
            rv += packPrice * item.count;
        });
        return rv;
    }

    const increaseCount = (item: CartItem) => {
        fetchItem(item.product, item.variant, item.selective, item.additives, 1);
    }

    const decreaseCount = (item: CartItem) => {
        fetchItem(item.product, item.variant, item.selective, item.additives, -1);
    }

    const deleteItem = (item: CartItem) => {
        fetchItem(item.product, item.variant, item.selective, item.additives, -item.count);
    }

    return <CartContext.Provider value={{
        cartItems: items,
        addToCart: add,
        removeFromCart: remove,
        getCount: count,
        isFetching: fetching,
        allItemsCount: allCount(),
        increase: increaseCount,
        decrease: decreaseCount,
        delete: deleteItem,
        allItemsPrice: allPriceWithDiscount(),
        allItemsPriceNoDiscount: allPriceWithoutDiscount(),
        totalCartPriceHasDiscount: allPriceWithDiscount() != allPriceWithoutDiscount()
    }}>
        {children}
    </CartContext.Provider>
}

function orderItemToCartItem(orderItem: OrderItem): CartItem {
    return {
        product: orderItem.product,
        variant: orderItem.variant,
        fullName: `${orderItem.product.name}${orderItem.variant ? ` (${orderItem.variant.name})` : ""} `,
        count: orderItem.count,
        additives: orderItem.additives,
        selective: orderItem.selective,
        singleItemSumPrice: (discount)=>{
            return cartItemSingleItemSum(orderItem.product, orderItem.variant, orderItem.selective, orderItem.additives, discount);
        },
        totalItemSumPrice: (discount) => {
            return cartItemSingleItemSum(orderItem.product, orderItem.variant, orderItem.selective, orderItem.additives, discount) * orderItem.count;
        }
    }
}

function cartItemSingleItemSum(product:Product, variant: Variant |null, selective: Selective|null, additives: Additive[], discount: boolean) {
    const productOrVariant = variant != null ? variant : product;
    const p =  discount ? productOrVariant.priceWithDiscount : productOrVariant.price;
    const s = selective ? selective.price : 0;
    let as = 0;
    additives?.map(item => {
        as += item.price
    });
    return p + s + as;
}